import * as moment from "moment";
import * as React from "react";
import DateTimeFormatter from "sh-application/utility/DateTimeFormatter";
import DateUtils from "sh-application/utility/DateUtils";
import StringsStore from "sh-strings/store";
import { DatePicker, DatePickerProps, CalendarStrings } from "@fluentui/react-datepicker-compat";
import { Moment } from "moment";

interface LocalizedDatePickerV2Props extends Omit<DatePickerProps, 'value' | 'onSelectDate' | 'formatDate' | 'parseDateFromString' | 'strings'> {
    title?: string;
    datePickerClassName?: string;
    value: moment.Moment;
    /** @deprecated All callers pass the team timezone (TimeZoneUtils.displayTimezone). Remove this prop and read the timezone directly in Phase 7. */
    timeZoneOlsonCode: string;
    onDateSelectedCallBack: (date: moment.Moment) => void;
    ariaLabelDate?: string;
    isRequired?: boolean;
    disabled?: boolean;
}

export const LocalizedDatePickerV2: React.FC<LocalizedDatePickerV2Props> = ({
    title,
    datePickerClassName,
    value,
    timeZoneOlsonCode,
    onDateSelectedCallBack,
    ariaLabelDate,
    isRequired,
    disabled,
    ...datePickerProps
}) => {
    const [isEmpty, setIsEmpty] = React.useState<boolean>(false);
    const teamsettingsStrings = StringsStore().registeredStringModules.get("teamsettings")?.strings;

    // Prepare localized calendar strings
    const calendarStrings: CalendarStrings = React.useMemo(
        () => ({
            months: DateTimeFormatter.getMonthNames("long"), // e.g., ["January", "February", ...]
            shortMonths: DateTimeFormatter.getMonthNames("short"), // e.g., ["Jan", "Feb", ...]
            days: DateTimeFormatter.getWeekdayNames("long"), // e.g., ["Sunday", "Monday", ...]
            shortDays: DateTimeFormatter.getWeekdayNames("short"), // e.g., ["Sun", "Mon", ...]
            goToToday: teamsettingsStrings?.get("goToToday") || "Go to today",
            prevMonthAriaLabel: teamsettingsStrings?.get("goToPreviousMonth") || "Previous month",
            nextMonthAriaLabel: teamsettingsStrings?.get("goToNextMonth") || "Next month",
            prevYearAriaLabel: teamsettingsStrings?.get("goToPreviousYear") || "Previous year",
            nextYearAriaLabel: teamsettingsStrings?.get("goToNextYear") || "Next year"
        }),
        [teamsettingsStrings]
    );

    // Convert timezone date to display date
    const displayDateValue: Date | null = React.useMemo(() => {
        if (!value) {
            return null;
        }
        return DateUtils.convertTimezoneDateTimeToDisplayTime(value.toDate());
    }, [value]);

    // Handle date selection
    const onDateChanged = React.useCallback(
        (date: Date | null | undefined) => {
            setIsEmpty(!date);

            if (date && onDateSelectedCallBack) {
                date.setHours(0, 0, 0, 0); // Clear the time portion

                // Convert display time back to timezone
                const dateSelectedInTimeZone = DateUtils.convertDisplayTimeToTimezoneDateTime(date);
                onDateSelectedCallBack(moment(dateSelectedInTimeZone).tz(timeZoneOlsonCode));
            }
        },
        [onDateSelectedCallBack, timeZoneOlsonCode]
    );

    // Handle blur event for required validation
    const onLostFocus = React.useCallback(() => {
        if (isRequired && isEmpty && value) {
            // Reset to the stored value if empty and required
            onDateChanged(value.toDate());
        }
    }, [isRequired, isEmpty, value, onDateChanged]);

    // Format date for display
    const formatDate = React.useCallback(
        (date: Date): string => {
            if (!date) {
                return "";
            }
            // The display Date's local components already represent the correct date in the picker's timezone.
            // Format those components directly without further timezone conversion.
            return DateTimeFormatter.formatBrowserDate(date); // e.g., "4/14/2026"
        },
        []
    );

    // Parse date string
    const parseDateFromString = React.useCallback(
        (dateStr: string): Date | null => {
            // The DatePicker expects a "display" Date whose local components show the date in the picker's timezone.
            if (dateStr && dateStr.length > 0) {
                const parsedDate = DateTimeFormatter.parseBrowserDate(dateStr);
                if (parsedDate) {
                    // Returns a Date with browser-local components matching what the user typed.
                    return parsedDate;
                }
            }
            // Invalid or empty input: fall back to the previously selected display date
            const fallbackDate = value ? value.toDate() : new Date();
            return DateUtils.convertTimezoneDateTimeToDisplayTime(fallbackDate);
        },
        [value]
    );

    // Placeholder text — show today's date in the picker's timezone
    const placeholder = DateTimeFormatter.formatBrowserDate(DateUtils.convertTimezoneDateTimeToDisplayTime(new Date())); // e.g., "4/14/2026"

    return (
        <DatePicker
            {...datePickerProps}
            disabled={disabled}
            className={datePickerClassName}
            allowTextInput
            value={displayDateValue}
            onSelectDate={onDateChanged}
            placeholder={placeholder}
            formatDate={formatDate}
            parseDateFromString={parseDateFromString}
            onBlur={onLostFocus}
            aria-label={ariaLabelDate}
            strings={calendarStrings}
        />
    );
};

export default LocalizedDatePickerV2;
